import { useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { LaunchpadTile } from "../Launchpad/Launchpad";
import { FeedRenderer } from "../Feed/FeedRenderer.js";
import type {
  FeedItemDefinition,
  ChatMessageFeedItem,
  TileGridFeedItem,
  RecommendedActionsFeedItem,
  FeedAction,
  FeedActionContext
} from "../Feed/feedTypes.js";
import type { FieldFormatDescriptor, FieldFormatKind } from "../../standalone/formatting.js";
import { fetchSession, type AuthSessionInfo, checkDatabaseConnection, getAuthHeaders, storeTokens } from "../../api/auth.js";
import { logout } from "../../api/auth";
import { fetchAvailableTenants, switchTenant, type TenantInfo } from "../../api/tenants";
import { listIdentityProviders, type IdentityProviderSummary } from "../../api/identityProviders";
import { clearTenantSlug, storeTenantSlug } from "../../tenants/tenantConfig";
import { createSession, sendMessage, listMessages, getRecommendedActions, type ChatMessage, type RecommendedActionsContext } from "../../api/chat.js";
import { WorkflowRenderer } from "../../workflow/WorkflowRenderer.js";
import type { WorkflowUIConfig } from "../../workflow/types.js";
import { LaunchpadContext } from "../Launchpad/LaunchpadContext";
import { AuthPanel } from "../AuthPanel";
import { login } from "../../api/auth";
import { API_BASE_URL } from "../../config";
import IntecLogo from "../intecLogo.svg?url";
import { authenticateTerminal, isTerminalMode } from "../../utils/terminalAuth.js";
import { getAppComponent } from "../../config/appRegistry.js";
import "./AIpad.css";

interface SessionStackItem {
  sessionId: string;
  title?: string;
}

type FieldFormatMap = Record<string, FieldFormatDescriptor>;

const KNOWN_FIELD_FORMAT_KINDS = new Set<FieldFormatKind>(["raw", "datetime", "language", "boolean", "number"]);

function normalizeFieldFormatDescriptor(value: unknown): FieldFormatDescriptor | undefined {
  if (!value) return undefined;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase() as FieldFormatKind;
    if (KNOWN_FIELD_FORMAT_KINDS.has(normalized)) {
      return { kind: normalized };
    }
    return undefined;
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    const descriptor = value as { kind?: unknown };
    if (typeof descriptor.kind === "string") {
      const normalized = descriptor.kind.trim().toLowerCase() as FieldFormatKind;
      if (KNOWN_FIELD_FORMAT_KINDS.has(normalized)) {
        return { kind: normalized };
      }
    }
  }
  return undefined;
}

function parseFieldFormats(source: unknown): FieldFormatMap | undefined {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return undefined;
  }
  const entries = Object.entries(source as Record<string, unknown>);
  const result: FieldFormatMap = {};
  for (const [key, descriptor] of entries) {
    if (typeof key !== "string") continue;
    const normalized = normalizeFieldFormatDescriptor(descriptor);
    if (normalized) {
      result[key] = normalized;
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function extractFieldFormatsFromPayload(payload: unknown): FieldFormatMap | undefined {
  if (!payload || typeof payload !== "object") {
    return undefined;
  }
  const direct = parseFieldFormats((payload as { fieldFormats?: unknown }).fieldFormats);
  if (direct) return direct;
  const plan = (payload as { presentationPlan?: unknown }).presentationPlan;
  if (!plan || typeof plan !== "object" || Array.isArray(plan)) {
    return undefined;
  }
  const metadata = (plan as { metadata?: unknown }).metadata;
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return undefined;
  }
  return parseFieldFormats((metadata as { fieldFormats?: unknown }).fieldFormats);
}

interface AIpadProps {
  tiles: LaunchpadTile[];
  children?: ReactNode;
  autoOpenFeed?: boolean; // Auto-open feed on mount (e.g., after SSO login)
}

/**
 * AuthLoginCard Component
 * Displays login form with email code authentication and SSO options
 */
function AuthLoginCard() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [loginMethod, setLoginMethod] = useState<"code" | "password">("code");
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [authFeedback, setAuthFeedback] = useState<{ type: "error" | "info"; message: string } | null>(null);
  const [ssoSubmitting, setSsoSubmitting] = useState<string | null>(null);
  const [identityProviders, setIdentityProviders] = useState<IdentityProviderSummary[]>([]);
  const [identityProvidersLoading, setIdentityProvidersLoading] = useState(false);
  const [hasSpecificTenant, setHasSpecificTenant] = useState(false);

  const handleEmailSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthSubmitting(true);
    setAuthFeedback(null);

    try {
      // Request email code
      const response = await fetch(`${API_BASE_URL}/api/auth/email-code/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Code-Anfrage fehlgeschlagen');
      }

      setAuthFeedback({
        type: "info",
        message: "Code wurde an deine E-Mail-Adresse gesendet"
      });
      setStep("code");
    } catch (error: any) {
      setAuthFeedback({
        type: "error",
        message: error?.message || "Code-Anfrage fehlgeschlagen"
      });
    } finally {
      setAuthSubmitting(false);
    }
  }, [email]);

  const handleCodeSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthSubmitting(true);
    setAuthFeedback(null);

    try {
      // Verify code
      const response = await fetch(`${API_BASE_URL}/api/auth/email-code/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), code: code.trim() })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Ungültiger Code');
      }

      const data = await response.json();

      // Speichere Token im localStorage (für Cross-Origin Requests)
      if (data.token) {
        // Nutze die zentrale storeTokens Funktion (speichert im Memory Cache + localStorage)
        console.log('[Email-Code-Auth] Storing token:', data.token.substring(0, 50));
        storeTokens(data.token, data.token); // Beide als Access und Refresh Token

        // Verifiziere, dass Token gespeichert wurde
        const stored = localStorage.getItem('ia_access_token');
        console.log('[Email-Code-Auth] Token stored successfully:', stored === data.token);
      }

      // Warte kurz, damit localStorage synchronisiert wird
      await new Promise(resolve => setTimeout(resolve, 100));

      // Login successful - fetch session to get user info
      console.log('[Email-Code-Auth] Login successful, fetching session...');
      const sessionInfo = await fetchSession();

      if (sessionInfo.authenticated && sessionInfo.user) {
        console.log('[Email-Code-Auth] Session loaded, user authenticated:', sessionInfo.user);

        // Set flag for auto-opening feed (same as SSO pattern)
        sessionStorage.setItem('sso-auto-open-feed', 'true');

        // Redirect directly to chat page (same as SSO pattern)
        const tenantId = sessionInfo.user.tenantId;
        console.log('[Email-Code-Auth] Redirecting to chat page:', `/tenants/${tenantId}/chat`);
        window.location.href = `/tenants/${tenantId}/chat`;
      } else {
        throw new Error('Session konnte nicht geladen werden');
      }
    } catch (error: any) {
      setAuthFeedback({
        type: "error",
        message: error?.message || "Ungültiger Code"
      });
    } finally {
      setAuthSubmitting(false);
    }
  }, [email, code]);

  const handlePasswordSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthSubmitting(true);
    setAuthFeedback(null);

    try {
      // Login with email and password
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), password: password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login fehlgeschlagen');
      }

      const data = await response.json();

      // Store tokens
      if (data.accessToken) {
        console.log('[Password-Auth] Storing tokens');
        storeTokens(data.accessToken, data.refreshToken || data.accessToken);
      }

      // Wait for localStorage sync
      await new Promise(resolve => setTimeout(resolve, 100));

      // Fetch session
      console.log('[Password-Auth] Login successful, fetching session...');
      const sessionInfo = await fetchSession();

      if (sessionInfo.authenticated && sessionInfo.user) {
        console.log('[Password-Auth] Session loaded, user authenticated:', sessionInfo.user);

        // Set flag for auto-opening feed
        sessionStorage.setItem('sso-auto-open-feed', 'true');

        // Redirect to chat page
        const tenantId = sessionInfo.user.tenantId;
        console.log('[Password-Auth] Redirecting to chat page:', `/tenants/${tenantId}/chat`);
        window.location.href = `/tenants/${tenantId}/chat`;
      } else {
        throw new Error('Session konnte nicht geladen werden');
      }
    } catch (error: any) {
      setAuthFeedback({
        type: "error",
        message: error?.message || "Login fehlgeschlagen"
      });
    } finally {
      setAuthSubmitting(false);
    }
  }, [email, password]);

  useEffect(() => {
    let isActive = true;
    const abortController = new AbortController();
    setIdentityProvidersLoading(true);

    // Extract tenantSlug from query parameter (?tenant=intecio) or pathname (/tenants/{tenantSlug}/chat)
    const urlParams = new URLSearchParams(window.location.search);
    let tenantSlug = urlParams.get('tenant') || undefined;

    // Fallback: Extract from pathname if not in query params
    if (!tenantSlug) {
      const pathParts = window.location.pathname.split('/');
      tenantSlug = pathParts[1] === 'tenants' && pathParts[2] ? pathParts[2] : undefined;
    }

    console.log('[AIpad] Loading identity providers for tenant:', tenantSlug);

    // Store whether we have a specific tenant for later use in provider filtering
    setHasSpecificTenant(Boolean(tenantSlug));

    listIdentityProviders({ tenantSlug }, undefined, abortController.signal)
      .then((providers) => {
        if (!isActive) return;
        console.log('[AIpad] Loaded providers:', providers.map(p => ({ slug: p.slug, displayName: p.displayName })));
        setIdentityProviders(providers);
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        // Ignore abort errors (expected when component unmounts)
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error("Failed to load identity providers:", error);
      })
      .finally(() => {
        if (isActive) {
          setIdentityProvidersLoading(false);
        }
      });

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, []);

  const handleSsoProviderClick = useCallback(async (provider: any) => {
    // Guard against double-clicks
    if (ssoSubmitting) {
      console.log('[SSO] Already submitting, ignoring duplicate click');
      return;
    }

    console.log('[SSO] Starting SSO flow for provider:', provider.slug);
    setSsoSubmitting(provider.slug);
    setAuthFeedback(null);

    try {
      const redirectUri = `${window.location.origin}/auth/sso-complete`;

      // Extract tenantSlug from query parameter (?tenant=intecio) or pathname (/tenants/{tenantSlug}/chat)
      const urlParams = new URLSearchParams(window.location.search);
      let tenantSlug = urlParams.get('tenant') || undefined;

      // Fallback: Extract from pathname if not in query params
      if (!tenantSlug) {
        const pathParts = window.location.pathname.split('/');
        tenantSlug = pathParts[1] === 'tenants' && pathParts[2] ? pathParts[2] : undefined;
      }

      console.log('[SSO] Requesting authorization for tenant:', tenantSlug);
      const response = await fetch(`${API_BASE_URL}/api/auth/providers/${provider.slug}/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          redirectUri,
          tenantSlug
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Authorization request failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.authorizationUrl) {
        throw new Error('No authorization URL received');
      }

      // Handle SAML POST binding (Auto-Submit Form)
      if (result.method === 'POST') {
        // Create hidden form for SAML POST binding
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = result.authorizationUrl;
        form.style.display = 'none';

        // Get form data from metadata
        const formData = result.metadata?.formData || {};

        // Add all form fields from metadata
        Object.entries(formData).forEach(([name, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value as string;
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
      } else {
        // Handle OIDC redirect (GET)
        window.location.href = result.authorizationUrl;
      }
    } catch (error) {
      console.error('SSO error:', error);
      setAuthFeedback({
        type: 'error',
        message: 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.'
      });
      setSsoSubmitting(null);
    }
  }, [ssoSubmitting]);

  const ssoProviders = useMemo(() => {
    if (identityProviders.length === 0) {
      return [];
    }

    // Sort by default flag
    const sortedProviders = identityProviders
      .slice()
      .sort((a, b) => Number(b.default) - Number(a.default));

    // If we have a specific tenant, only show the first (default) provider
    // If no tenant is specified, show all providers
    const providersToShow = hasSpecificTenant ? [sortedProviders[0]] : sortedProviders;

    return providersToShow
      .filter(Boolean)
      .map((provider) => {
        const buttonMetadata = provider.metadata?.button ?? null;
        const buttonText = typeof buttonMetadata?.text === "string" && buttonMetadata.text.trim().length > 0
          ? buttonMetadata.text.trim()
          : provider.displayName;

        return {
          slug: provider.slug,
          displayName: provider.displayName,
          buttonText,
          accentColor: buttonMetadata?.accentColor,
          icon: buttonMetadata?.icon ?? null
        };
      });
  }, [identityProviders, hasSpecificTenant]);

  return (
    <div className="auth-login-card">
      {/* Email Code Section */}
      <div className="auth-email-code-section">
        <h3 className="auth-section-title">Mit E-Mail anmelden</h3>

        {/* Login Method Toggle */}
        {step === "email" && (
          <div className="auth-method-toggle">
            <button
              type="button"
              className={`auth-method-button ${loginMethod === "code" ? "active" : ""}`}
              onClick={() => {
                setLoginMethod("code");
                setPassword("");
                setAuthFeedback(null);
              }}
            >
              Mit Code
            </button>
            <button
              type="button"
              className={`auth-method-button ${loginMethod === "password" ? "active" : ""}`}
              onClick={() => {
                setLoginMethod("password");
                setCode("");
                setAuthFeedback(null);
              }}
            >
              Mit Passwort
            </button>
          </div>
        )}

        {authFeedback && (
          <div className={`auth-feedback ${authFeedback.type}`}>
            {authFeedback.message}
          </div>
        )}

        {step === "email" ? (
          <>
            {loginMethod === "code" ? (
              <form onSubmit={handleEmailSubmit} className="auth-email-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-Mail-Adresse eingeben"
                  required
                  disabled={authSubmitting}
                  className="auth-email-input"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={authSubmitting || !email.trim()}
                  className="auth-submit-button"
                >
                  {authSubmitting ? "Sende Code..." : "Code per E-Mail senden"}
                </button>
              </form>
            ) : (
              <form onSubmit={handlePasswordSubmit} className="auth-email-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-Mail-Adresse eingeben"
                  required
                  disabled={authSubmitting}
                  className="auth-email-input"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Passwort eingeben"
                  required
                  disabled={authSubmitting}
                  className="auth-password-input"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={authSubmitting || !email.trim() || !password}
                  className="auth-submit-button"
                >
                  {authSubmitting ? "Anmeldung..." : "Anmelden"}
                </button>
              </form>
            )}
          </>
        ) : (
          <form onSubmit={handleCodeSubmit} className="auth-code-form">
            <div className="auth-code-info">
              Code wurde an <strong>{email}</strong> gesendet
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setAuthFeedback(null);
                }}
                className="auth-change-email"
              >
                Ändern
              </button>
            </div>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-stelliger Code"
              required
              disabled={authSubmitting}
              className="auth-code-input"
              autoFocus
              maxLength={6}
            />
            <button
              type="submit"
              disabled={authSubmitting || code.length !== 6}
              className="auth-submit-button"
            >
              {authSubmitting ? "Prüfe Code..." : "Anmelden"}
            </button>
          </form>
        )}
      </div>

      {/* SSO Section - Show only on email step */}
      {step === "email" && ssoProviders.length > 0 && (
        <>
          {/* Divider */}
          <div className="auth-divider">
            <span>oder</span>
          </div>

          <div className="auth-sso-section">
            <h3 className="auth-section-title">Mit Microsoft anmelden</h3>
            {ssoProviders.map((provider) => {
              const isSubmitting = ssoSubmitting === provider.slug;
              const isDisabled = authSubmitting || Boolean(ssoSubmitting);
              const style = provider.accentColor
                ? { borderColor: provider.accentColor, color: provider.accentColor }
                : undefined;
              return (
                <button
                  key={provider.slug}
                  type="button"
                  className="auth-sso-button"
                  style={style}
                  onClick={() => handleSsoProviderClick(provider)}
                  disabled={isDisabled}
                >
                  <span className="auth-sso-icon-wrapper">
                    {provider.icon === "microsoft" ? (
                      <svg className="auth-sso-icon" viewBox="0 0 24 24" aria-hidden="true">
                        <rect x="2" y="2" width="9" height="9" fill="#f35325" />
                        <rect x="13" y="2" width="9" height="9" fill="#81bc06" />
                        <rect x="2" y="13" width="9" height="9" fill="#05a6f0" />
                        <rect x="13" y="13" width="9" height="9" fill="#ffba08" />
                      </svg>
                    ) : (
                      <span className="auth-sso-icon-text">SSO</span>
                    )}
                  </span>
                  <span>{isSubmitting ? "Weiterleitung…" : provider.buttonText}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function AIpad({ tiles, children, autoOpenFeed }: AIpadProps) {
  const location = useLocation();
  const navigate = useNavigate();

  // Header state
  const [session, setSession] = useState<AuthSessionInfo | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [availableTenants, setAvailableTenants] = useState<TenantInfo[]>([]);
  const [showTenantSwitcher, setShowTenantSwitcher] = useState(false);
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitleValue, setEditedTitleValue] = useState<string>("");
  const [currentDateTime, setCurrentDateTime] = useState<string>("");
  const [titleHandlers, setTitleHandlers] = useState<{
    onClick: () => void;
    onChange: (value: string) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onBlur: () => void;
  } | null>(null);
  const [cardMenuActions, setCardMenuActions] = useState<Array<{
    icon: string;
    label: string;
    onClick: () => void;
    danger?: boolean;
  }> | null>(null);
  const [showCardMenu, setShowCardMenu] = useState(false);

  // Startup UI State - start with true to skip second blue dot
  const [showLoginPrompt, setShowLoginPrompt] = useState(true);

  // Multi-Feed System State
  interface FeedSession {
    id?: string;
    sessionId?: string;
    feedItems: FeedItemDefinition[];
    title: string;
    isLoading?: boolean;
  }

  const [feedSessions, setFeedSessions] = useState<FeedSession[]>([]);
  const [currentFeedIndex, setCurrentFeedIndex] = useState(0);
  const [recommendedActions, setRecommendedActions] = useState<Array<string | { text: string; isApp?: boolean }>>([]);
  const [loadingRecommendedActions, setLoadingRecommendedActions] = useState(false);
  const [showFooterSearch, setShowFooterSearch] = useState(false);
  const [footerSearchText, setFooterSearchText] = useState('');
  const [isClosingPopup, setIsClosingPopup] = useState(false);
  const [currentAction, setCurrentAction] = useState<string>('');
  const [autocompleteApps, setAutocompleteApps] = useState<string[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedAutocompleteIndex, setSelectedAutocompleteIndex] = useState(0);

  // Filter apps for autocomplete based on input text
  const getFilteredApps = useCallback((inputText: string) => {
    console.log('[Autocomplete] Input:', inputText, 'Length:', inputText.length);

    if (!inputText.trim() || inputText.length < 2) {
      console.log('[Autocomplete] Too short, hiding');
      setShowAutocomplete(false);
      return [];
    }

    const searchLower = inputText.toLowerCase();

    // Use recommendedActions that are apps (isApp: true)
    const availableApps = recommendedActions
      .filter(action => typeof action === 'object' && action.isApp)
      .map(action => typeof action === 'object' ? action.text : action);

    console.log('[Autocomplete] Available apps from recommendedActions:', availableApps);

    const filtered = availableApps.filter(app =>
      app.toLowerCase().includes(searchLower)
    ).slice(0, 5); // Max 5 suggestions

    console.log('[Autocomplete] Filtered apps:', filtered, 'Show:', filtered.length > 0);
    setShowAutocomplete(filtered.length > 0);
    setSelectedAutocompleteIndex(0);
    return filtered;
  }, [recommendedActions]);  // Helper function to close popup with animation
  const closePopup = useCallback(() => {
    setIsClosingPopup(true);
    setTimeout(() => {
      setShowFooterSearch(false);
      setFooterSearchText('');
      setShowAutocomplete(false);
      setIsClosingPopup(false);
    }, 300);
  }, []);

  // Legacy state (being migrated to feedSessions)
  const [sessionStack, setSessionStack] = useState<SessionStackItem[]>([]);
  const [feedItems, setFeedItems] = useState<FeedItemDefinition[]>([]);
  const [tileFilters, setTileFilters] = useState<Record<string, string>>({});
  const [userName, setUserName] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string>("");
  const [workspaceName, setWorkspaceName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamTitle, setStreamTitle] = useState<string>("");
  const [isEditingStreamTitle, setIsEditingStreamTitle] = useState(false);
  const [editedStreamTitle, setEditedStreamTitle] = useState<string>("");
  const [menuCardId, setMenuCardId] = useState<string | null>(null);
  const [notificationCardId, setNotificationCardId] = useState<string | null>(null);
  const [newCardId, setNewCardId] = useState<string | null>(null);
  const [allChatQuestions, setAllChatQuestions] = useState<string[]>([]);

  // Simple Input State
  const [currentInput, setCurrentInput] = useState('');

  // Fullscreen mode state
  const [isFullscreenMode, setIsFullscreenMode] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  // Footer minimized state
  const [isFooterMinimized, setIsFooterMinimized] = useState(false);

  // Terminal Authentication - Parse URL Parameters
  const terminalParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const apiKey = params.get("apiKey");
    const tenant = params.get("tenant"); // Could be tenantId or slug
    const app = params.get("app");

    return { apiKey, tenant, app };
  }, []);

  // Terminal Authentication - Execute on mount if parameters present
  useEffect(() => {
    const { apiKey, tenant, app } = terminalParams;

    // Check if we need to authenticate
    const hasApiKey = Boolean(apiKey);
    const hasTenant = Boolean(tenant);
    const hasApp = Boolean(app);
    const alreadyInTerminalMode = isTerminalMode();

    // Skip if already authenticated or missing params
    if (alreadyInTerminalMode) {
      return;
    }

    if (!hasApiKey || !hasTenant || !app) {
      return;
    }

    // Execute authentication
    let isMounted = true;

    const performAuth = async () => {
      try {
        // Ensure apiKey and tenant are not null before calling authenticateTerminal
        if (!apiKey || !tenant) {
          console.error("[Terminal Auth] Missing apiKey or tenant");
          return;
        }

        // Call authenticateTerminal from terminalAuth.ts
        await authenticateTerminal(apiKey, tenant);

        if (!isMounted) return;

        // Remove apiKey from URL for security
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("apiKey");
        window.history.replaceState({}, "", newUrl.toString());

        // Reload to trigger terminal mode UI
        window.location.reload();
      } catch (error) {
        console.error("[Terminal Auth] Authentication failed:", error);

        if (!isMounted) return;

        // Show error to user
        alert(
          `Terminal-Authentifizierung fehlgeschlagen:\n\n${error instanceof Error ? error.message : "Unbekannter Fehler"
          }\n\nBitte überprüfe den API-Key und versuche es erneut.`
        );
      }
    };

    performAuth();

    return () => {
      isMounted = false;
    };
  }, [terminalParams]);

  // Load session on mount
  useEffect(() => {
    let mounted = true;

    const loadSession = async () => {
      try {
        // Skip session loading if terminal authentication is in progress
        const { apiKey, tenant, app } = terminalParams;
        const hasTerminalParams = Boolean(apiKey) && Boolean(tenant) && Boolean(app);
        const alreadyInTerminalMode = isTerminalMode();

        if (hasTerminalParams && !alreadyInTerminalMode) {
          return;
        }

        // Clear all cookies and logout from SSO on startup
        // This ensures a clean start without automatic login
        const hasInitializedCleanStart = sessionStorage.getItem('cleanStartInitialized');
        const ssoAutoOpenFlag = sessionStorage.getItem('sso-auto-open-feed');

        if (!hasInitializedCleanStart && !ssoAutoOpenFlag) {
          // Only do clean start if NOT coming from SSO
          // Clear all cookies
          document.cookie.split(";").forEach((c) => {
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
          });

          // Clear session storage except our flags
          const keysToKeep = ['cleanStartInitialized', 'sso-auto-open-feed'];
          Object.keys(sessionStorage).forEach(key => {
            if (!keysToKeep.includes(key)) {
              sessionStorage.removeItem(key);
            }
          });

          // Clear local storage auth tokens
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');

          // Mark that we've done the clean start
          sessionStorage.setItem('cleanStartInitialized', 'true');

          // Force logout if there's any active session
          try {
            await logout();
          } catch (err) {
            console.log("No active session to logout from");
          }
        } else if (ssoAutoOpenFlag === 'true') {
          // Coming from SSO - skip clean start, mark as initialized
          sessionStorage.setItem('cleanStartInitialized', 'true');
          // Set sessionLoading to false immediately to skip spinner and show feed
          if (mounted) {
            setSessionLoading(false);
          }
        }

        const sessionData = await fetchSession();
        if (!mounted) return;

        if (sessionData && sessionData.authenticated && sessionData.user) {
          // Redirect to main page if on SSO callback route
          if (window.location.pathname === "/auth/sso-complete") {
            console.log("[AIpad] Authenticated on SSO callback route, redirecting to main page");
            window.location.replace("/");
            return;
          }

          setSession(sessionData);
          setUserName(sessionData.user.displayName || sessionData.user.email || "");
          setUserEmail(sessionData.user.email || "");

          // Load tenants to get the actual tenant name (only if authenticated and NOT terminal)
          // Terminals don't need stream titles or tenant switching
          const isTerminal = sessionData.user?.roles?.includes('terminal');
          if (!isTerminal) {
            try {
              const tenants = await fetchAvailableTenants();
              console.log('[AIpad] Fetched available tenants:', tenants.length, tenants.map(t => t.tenantName));
              setAvailableTenants(tenants); // IMPORTANT: Store tenants in state for "set tenant" command
              const currentTenant = tenants.find(t => t.tenantId === sessionData.user?.tenantId);
              const tenantName = currentTenant?.tenantName || sessionData.user?.tenantId || "Workspace";

              // Initialize stream title with tenant name + date/time
              const now = new Date();
              const dateStr = now.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
              const timeStr = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
              const initialTitle = `${tenantName} - ${dateStr} ${timeStr}`;
              setStreamTitle(initialTitle);
              setEditedStreamTitle(initialTitle);
            } catch (err) {
              console.error("Failed to fetch tenants for title:", err);
              // Fallback to tenantId if tenant fetch fails
              const tenantName = sessionData.user?.tenantId || "Workspace";
              const now = new Date();
              const dateStr = now.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
              const timeStr = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
              const initialTitle = `${tenantName} - ${dateStr} ${timeStr}`;
              setStreamTitle(initialTitle);
              setEditedStreamTitle(initialTitle);
            }
          }
        } else {
          setSession({ authenticated: false, user: null });
        }
      } catch (err) {
        console.error("Failed to load session:", err);

        // Don't set error state if terminal authentication is pending
        const { apiKey } = terminalParams;
        if (!apiKey) {
          setSession({ authenticated: false, user: null });
        }
      } finally {
        if (mounted) {
          setSessionLoading(false);
        }
      }
    };

    loadSession();

    return () => {
      mounted = false;
    };
  }, []);

  // Auto-open feed after SSO login OR Email-Code-Login
  useEffect(() => {
    // Check if user just logged in via SSO or Email-Code (uses same flag)
    // Note: sessionStorage flag is set before redirect in both auth flows
    if (session?.authenticated && autoOpenFeed) {
      console.log('[AIpad] Auto-opening feed after authentication');
      setShowLoginPrompt(true);
    }
  }, [autoOpenFeed, session?.authenticated]);

  // Auto-create empty session after successful login (no initial AICard)
  useEffect(() => {
    // Skip if session is still loading or not authenticated
    if (sessionLoading || !session?.authenticated) {
      return;
    }

    // Check if this is initial load after login (no feed sessions yet)
    if (feedSessions.length === 0) {
      console.log('[AIpad] Creating initial empty session after login');

      // Create backend session only (no AICard with suggestions)
      (async () => {
        const backendSession = await createSession();

        // Create new empty feed with backend session ID
        const newSession: FeedSession = {
          id: `feed-${Date.now()}`,
          sessionId: backendSession.sessionId,
          feedItems: [], // Empty - no initial suggestions
          title: 'AI Assistant'
        };

        setFeedSessions([newSession]);
        setCurrentFeedIndex(0);
      })();
    }
  }, [sessionLoading, session, feedSessions.length]);

  // Keyboard navigation for fullscreen mode
  useEffect(() => {
    if (!isFullscreenMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && currentCardIndex > 0) {
        e.preventDefault();
        setCurrentCardIndex(currentCardIndex - 1);
      } else if (e.key === 'ArrowRight' && currentCardIndex < feedItems.length - 1) {
        e.preventDefault();
        setCurrentCardIndex(currentCardIndex + 1);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsFullscreenMode(false);
        setCurrentCardIndex(0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreenMode, currentCardIndex, feedItems.length]);

  // Listen for NAVIGATE_TO_APP messages from backend responses
  useEffect(() => {
    const handleNavigateMessage = (event: MessageEvent) => {
      console.log('[AIpad] Message event received:', {
        type: event.data?.type,
        data: event.data,
        origin: event.origin,
        source: event.source
      });

      if (event.data?.type === 'NAVIGATE_TO_APP') {
        const { path, title } = event.data;
        console.log('[AIpad] Navigate to app message received:', { path, title });
        console.log('[AIpad] Available tiles:', tiles.map(t => ({ id: t.id, title: t.title, path: t.path })));
        console.log('[AIpad] Current feed index:', currentFeedIndex);
        console.log('[AIpad] Feed sessions count:', feedSessions.length);

        // Find matching tile (optional - we can navigate even without a matching tile)
        const matchingTile = tiles.find(tile => tile.path === path);

        if (matchingTile) {
          console.log('[AIpad] Found matching tile:', matchingTile);
        } else {
          console.log('[AIpad] No matching tile found, creating app card anyway');
        }

        // Create iframe card for the app (works with or without matching tile)
        const appCard: FeedItemDefinition = {
          id: `app-${Date.now()}`,
          type: 'custom',
          title: title || matchingTile?.title || 'Application',
          layout: { columnSpan: 12 },
          config: {
            render: () => (
              <div style={{ width: '100%', height: '80vh' }}>
                <iframe
                  src={path}
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
                  style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px' }}
                  title={title}
                  onLoad={(e) => {
                    try {
                      const iframe = e.target as HTMLIFrameElement;
                      if (iframe.contentWindow) {
                        Object.defineProperty(iframe.contentWindow, 'top', {
                          get: () => iframe.contentWindow,
                          configurable: false
                        });
                      }
                    } catch (err) {
                      console.warn('Could not sandbox iframe navigation:', err);
                    }
                  }}
                />
              </div>
            )
          }
        };

        console.log('[AIpad] Adding app card to feed:', appCard);

        // Add to current feed
        setFeedSessions(prev => {
          console.log('[AIpad] Previous feed sessions:', prev.length);
          const updated = prev.map((feed, idx) => {
            if (idx === currentFeedIndex) {
              console.log('[AIpad] Updating feed at index', idx, 'with new app card');
              return { ...feed, feedItems: [...feed.feedItems, appCard] };
            }
            return feed;
          });
          console.log('[AIpad] Updated feed sessions:', updated.length);
          return updated;
        });
      }
    };

    console.log('[AIpad] Setting up message listener');
    window.addEventListener('message', handleNavigateMessage);
    return () => {
      console.log('[AIpad] Removing message listener');
      window.removeEventListener('message', handleNavigateMessage);
    };
  }, [tiles, currentFeedIndex]);

  // Handler: Submit action to feed (used by Home button and AI cards)
  const handleSubmitActionToFeed = useCallback(async (sessionId: string, action: string, feedIndex: number) => {
    console.log('[AIpad] AI Card operation executed:', action);

    // Check if it's the console command (handle locally without sending to backend)
    const actionLower = action.toLowerCase().trim();
    if (actionLower === 'start console' || actionLower === 'console') {
      console.log('[AIpad] Console command detected - navigating to /console');
      navigate('/console');
      return;
    }

    // Setze currentAction für Processing Ring Overlay
    setCurrentAction(action);

    // Erstelle eine neue leere Seite mit nur dem Kommando als Titel und Loading State
    const newFeed: FeedSession = {
      sessionId: sessionId,
      title: action,
      feedItems: [],
      isLoading: true
    };

    // Füge neue Feed-Seite hinzu und wechsle zu ihr
    setFeedSessions(prev => [...prev, newFeed]);
    setCurrentFeedIndex(feedSessions.length);

    try {
      // Send message to chat
      const response = await sendMessage(sessionId, action);

      // Update suggested actions from response
      if (response.suggestedActions && response.suggestedActions.length > 0) {
        setRecommendedActions(response.suggestedActions);
      }

      // Get the assistant message (last message in response)
      const assistantMsg = response.messages.find(msg => msg.role === 'assistant');

      if (assistantMsg) {
        // Escape HTML in action text to prevent HTML injection
        const escapeHtml = (text: string) => {
          const div = document.createElement('div');
          div.textContent = text;
          return div.innerHTML;
        };

        // Remove leading/trailing quotes from backend response if present
        let backendContent = assistantMsg.content;
        if (backendContent.startsWith('"') && backendContent.endsWith('"')) {
          backendContent = backendContent.slice(1, -1);
        }

        // Unescape JSON string escapes (e.g., \" to ", \n to newline)
        backendContent = backendContent.replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\\\/g, '\\');

        // For app start intents (iframe), skip the action header and separator to save space
        const isAppStart = assistantMsg.intent?.kind === 'start';
        const formattedContent = isAppStart
          ? backendContent
          : `<div style="font-size: 1.25rem; font-weight: 600; color: #1f2937; margin-bottom: 1rem;">${escapeHtml(action)}</div><hr style="border: none; border-top: 2px solid #e5e7eb; margin: 1.5rem 0;" />${backendContent}`;

        // Check if response contains workflow UI
        const payload = assistantMsg.payload as any;
        const hasWorkflowUI = payload?.ui && typeof payload.ui === 'object' && Array.isArray(payload.ui.steps);

        console.log('[AIpad] Processing assistant message:', {
          hasPayload: !!payload,
          hasUI: !!payload?.ui,
          uiType: typeof payload?.ui,
          hasSteps: payload?.ui?.steps ? 'yes' : 'no',
          isStepsArray: Array.isArray(payload?.ui?.steps),
          hasWorkflowUI,
          payloadKeys: payload ? Object.keys(payload) : [],
          hasMetadata: !!assistantMsg.metadata,
          metadata: assistantMsg.metadata,
          fullMessage: assistantMsg
        });

        // Erstelle ein einfaches Text-Item mit rohem Content vom Backend
        const resultItem: ChatMessageFeedItem = {
          id: assistantMsg._id,
          type: 'chat-message',
          title: 'Ergebnis',
          layout: { columnSpan: 12 },
          config: {
            question: '',
            answer: formattedContent,
            timestamp: new Date(assistantMsg.createdAt),
            displayMode: 'full' as const,
            messagePayload: {
              ...assistantMsg.payload,
              metadata: assistantMsg.metadata
            },
            // Add workflowUI to config if present in payload
            ...(hasWorkflowUI ? { workflowUI: payload.ui } : {})
          }
        };

        // Update die neue Seite mit dem Ergebnis
        setFeedSessions(prev => prev.map((feed, idx) =>
          idx === feedSessions.length
            ? {
              ...feed,
              feedItems: [resultItem],
              isLoading: false
            }
            : feed
        ));
      }

      // Clear currentAction
      setCurrentAction('');

      // Update chat questions history
      setAllChatQuestions(prev => [...prev, action]);
    } catch (error) {
      console.error('[AIpad] Failed to submit action:', error);
      setError('Fehler beim Senden der Nachricht');
      setCurrentAction('');

      // Update die neue Seite auf Error-State
      setFeedSessions(prev => prev.map((feed, idx) =>
        idx === feedSessions.length
          ? {
            ...feed,
            isLoading: false
          }
          : feed
      ));
    }
  }, [feedSessions, navigate]);

  // Register global handleRowClick function for HTML table row clicks
  useEffect(() => {
    (window as any).handleRowClick = (command: string) => {
      console.log('[AIpad] Row clicked, executing command:', command);
      const currentFeed = feedSessions[currentFeedIndex];
      if (currentFeed?.sessionId) {
        handleSubmitActionToFeed(currentFeed.sessionId, command, currentFeedIndex);
      }
    };

    return () => {
      delete (window as any).handleRowClick;
    };
  }, [feedSessions, currentFeedIndex, handleSubmitActionToFeed]);

  // Handler: Process command - Check if message is a command and handle it
  const processCommand = useCallback(async (message: string): Promise<boolean> => {
    const messageLower = message.toLowerCase().trim();
    console.log('[AIpad] processCommand called with:', message, 'lowercase:', messageLower);

    // Handle "start console" or "console" command - open console in full screen
    if (messageLower === 'start console' || messageLower === 'console') {
      console.log('[AIpad] Console command MATCHED - navigating to /console');
      console.log('[AIpad] Navigate function:', typeof navigate);
      navigate('/console');
      console.log('[AIpad] Navigate called');
      return true; // Command was handled, don't send to backend
    }

    // Handle "start <AppName>" command
    if (messageLower.startsWith('start ') || messageLower.startsWith('starten ')) {
      const appNameQuery = messageLower.replace(/^(start|starten)\s+/i, '').trim();
      console.log('[AIpad] Start command detected for app:', appNameQuery);

      // Find matching app by title or name
      const matchingApp = tiles.find(tile =>
        tile.title.toLowerCase().includes(appNameQuery) ||
        tile.id.toLowerCase().includes(appNameQuery) ||
        tile.path.toLowerCase().includes(appNameQuery)
      );

      if (matchingApp) {
        console.log('[AIpad] Starting application directly:', matchingApp.title, matchingApp.path);

        // Navigate directly to the application path without showing overlay
        navigate(matchingApp.path);

        return true; // Command was handled
      } else {
        console.log('[AIpad] No matching app found for:', appNameQuery);
        // Let backend handle it - it might find the app via database
        return false;
      }
    }

    // Handle application path as command (e.g., "/yard-list", "/users")
    const matchingTile = tiles.find(tile =>
      tile.path.toLowerCase() === messageLower ||
      tile.path.toLowerCase() === `/${messageLower}` ||
      tile.title.toLowerCase() === messageLower
    );

    if (matchingTile) {
      console.log('[AIpad] Application path command detected:', matchingTile.path);

      // Launch the application in an iframe
      const appCard: FeedItemDefinition = {
        id: `app-${Date.now()}`,
        type: 'custom',
        title: matchingTile.title,
        layout: { columnSpan: 12 },
        config: {
          render: () => (
            <div style={{ width: '100%', height: '80vh' }}>
              <iframe
                src={matchingTile.path}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
                style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px' }}
                title={matchingTile.title}
                onLoad={(e) => {
                  try {
                    const iframe = e.target as HTMLIFrameElement;
                    if (iframe.contentWindow) {
                      Object.defineProperty(iframe.contentWindow, 'top', {
                        get: () => iframe.contentWindow,
                        configurable: false
                      });
                    }
                  } catch (err) {
                    console.warn('Could not sandbox iframe navigation:', err);
                  }
                }}
              />
            </div>
          )
        }
      };

      // Add to current feed
      setFeedSessions(prev => prev.map((feed, idx) =>
        idx === currentFeedIndex
          ? { ...feed, feedItems: [...feed.feedItems, appCard] }
          : feed
      ));

      return true; // Command was handled
    }

    // Handle "launchpad" command
    if (messageLower === 'launchpad') {
      console.log('[AIpad] Launchpad command detected - showing apps');

      // Show launchpad tiles (clear screen and show only apps)
      const tenantName = availableTenants.find(t => t.tenantId === session?.user?.tenantId)?.tenantName || 'Anwendungen';
      const appsCard: FeedItemDefinition = {
        id: 'launchpad-tiles',
        type: 'tiles',
        title: tenantName,
        layout: { columnSpan: 12 },
        config: {
          columns: 3,
          tiles: tiles.map(tile => ({
            id: tile.id,
            label: tile.title,
            description: tile.description,
            action: {
              id: `launch-${tile.id}`,
              type: 'navigate',
              target: tile.path
            }
          }))
        }
      };

      // Create new feed with only the launchpad (clear screen)
      const launchpadFeed: FeedSession = {
        id: `launchpad-feed-${Date.now()}`,
        feedItems: [appsCard],
        title: 'Launchpad'
      };

      setFeedSessions([launchpadFeed]);
      setCurrentFeedIndex(0);

      return true; // Command was handled
    }

    // Handle "show <term>" command - search and display item(s)
    // If multiple results found: show as list
    // If single result found: show as detail view
    if (messageLower.startsWith('show ')) {
      console.log('[AIpad] Show command detected:', message);

      // Let it be sent to backend - AI will search and decide between list/detail view
      return false; // Let it be sent to backend as a normal message
    }

    // Handle "list" or "list of <type>" command - show all objects of a specific type from database
    // Examples: "list tenant", "list of user", "list yardDocument", "tenants", "users"
    // All list commands are sent to backend, which will apply tenant filtering except for tenant queries
    const listOfMatch = messageLower.match(/^(?:list\s+(?:of\s+)?|liste\s+(?:von\s+)?)?([\wäöüß]+?)s?$/);
    if (listOfMatch) {
      const typeName = listOfMatch[1];

      // Common type names that should be queried from database
      const commonTypes = [
        'tenant', 'user', 'yarddocument', 'transport', 'contract',
        'businesspartner', 'visit', 'hardware', 'app',
        'mieter', 'nutzer', 'benutzer', 'dokument', 'transporte'
      ];

      if (commonTypes.includes(typeName.toLowerCase())) {
        console.log('[AIpad] List command detected for type:', typeName, '- sending to backend');

        // Send to backend - backend will handle tenant filtering:
        // - tenant queries: NO tenant filtering (show all tenants)
        // - all other queries: WITH tenant filtering (only current tenant)
        return false; // Let it be sent to backend as a normal message
      }
    }

    // Handle "set tenant" command
    if (messageLower.startsWith('set tenant ')) {
      const tenantQuery = message.substring(11).trim();

      console.log('[AIpad] Set tenant command detected:', tenantQuery);
      console.log('[AIpad] Available tenants:', availableTenants.length, availableTenants.map(t => ({ name: t.tenantName, id: t.tenantId, slug: t.tenantSlug })));

      // If no tenants loaded yet, fetch them now
      let tenantsToSearch = availableTenants;
      if (tenantsToSearch.length === 0) {
        console.log('[AIpad] No tenants in state, fetching now...');
        try {
          tenantsToSearch = await fetchAvailableTenants();
          setAvailableTenants(tenantsToSearch);
          console.log('[AIpad] Fetched tenants:', tenantsToSearch.length, tenantsToSearch.map(t => t.tenantName));
        } catch (err) {
          console.error('[AIpad] Failed to fetch tenants:', err);
          setError('Fehler beim Laden der verfügbaren Tenants');
          setTimeout(() => setError(null), 5000);
          return true;
        }
      }

      // Find tenant by name or ID
      const targetTenant = tenantsToSearch.find(t =>
        t.tenantName.toLowerCase().includes(tenantQuery.toLowerCase()) ||
        t.tenantId === tenantQuery ||
        t.tenantSlug === tenantQuery
      );

      console.log('[AIpad] Target tenant found:', targetTenant ? targetTenant.tenantName : 'NOT FOUND');

      if (targetTenant) {
        console.log('[AIpad] Switching to tenant:', targetTenant.tenantName);
        try {
          await handleTenantSwitch(targetTenant.tenantId);
        } catch (err) {
          console.error('[AIpad] Tenant switch failed:', err);
          setError(`Tenant-Wechsel fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
          setTimeout(() => setError(null), 5000);
        }
      } else {
        const errorMsg = `Tenant "${tenantQuery}" nicht gefunden. Verfügbare: ${tenantsToSearch.map(t => t.tenantName).join(', ')}`;
        console.error('[AIpad]', errorMsg);
        setError(errorMsg);
        setTimeout(() => setError(null), 5000);
      }
      return true; // Command was handled
    }

    // Handle "logout" command - Send to backend instead of handling directly
    // Backend will return a logout intent with action: "logout" payload
    if (messageLower === 'logout' || messageLower === 'abmelden' || messageLower === 'log out') {
      console.log('[AIpad] Logout command detected - sending to backend');
      return false; // Send to backend
    }

    return false; // Not a command
  }, [availableTenants, session, tiles, currentFeedIndex, navigate]);

  // Handler: Tenant Switch - Switch to different tenant
  const handleTenantSwitch = async (tenantId: string) => {
    console.log('[AIpad] handleTenantSwitch called with tenantId:', tenantId);
    console.log('[AIpad] isSwitchingTenant:', isSwitchingTenant);

    if (isSwitchingTenant) {
      console.log('[AIpad] Already switching tenant, returning early');
      return;
    }

    setIsSwitchingTenant(true);
    setShowTenantSwitcher(false);

    try {
      console.log('[AIpad] Calling switchTenant API...');
      const response = await switchTenant(tenantId);
      console.log('[AIpad] switchTenant API response:', response);

      storeTenantSlug(response.tenantId);
      console.log('[AIpad] Stored tenant slug');

      // Reload user profile from database to get fresh tenant data
      console.log('[AIpad] Reloading user profile from database...');
      const freshSession = await fetchSession();
      console.log('[AIpad] Fresh user data loaded:', freshSession.user);

      console.log('[AIpad] Reloading page...');
      // Force hard reload to ensure new session with updated tenant
      // This ensures JWT tokens are refreshed and isPrimary flags are correct
      window.location.href = window.location.pathname;
    } catch (err) {
      console.error('[AIpad] Tenant switch failed:', err);
      const errorMessage = err instanceof Error ? err.message : "Unbekannter Fehler";
      alert(`Workspace-Wechsel fehlgeschlagen: ${errorMessage}`);
      setIsSwitchingTenant(false);
    }
  };

  // Handler: Home Button - Execute "launchpad" command
  const handleHomeButton = useCallback(async () => {
    console.log('[AIpad] Home button clicked - executing launchpad command');

    // Set input to "launchpad"
    setCurrentInput('launchpad');

    // Process the command directly (check if it's a command first)
    const isCommand = await processCommand('launchpad');
    if (isCommand) {
      return; // Command was handled locally
    }

    // If not a local command, send to backend
    try {
      const newSession = await createSession();
      const response = await sendMessage(newSession.sessionId, 'launchpad');

      if (response.messages && response.messages.length > 0) {
        const firstResponse = response.messages[0];

        // Create feed item from response
        const resultItem: ChatMessageFeedItem = {
          id: firstResponse._id || `msg-${Date.now()}`,
          type: 'chat-message',
          title: 'Antwort',
          layout: { columnSpan: 12 },
          config: {
            question: 'launchpad',
            answer: firstResponse.content,
            timestamp: new Date(firstResponse.createdAt || Date.now()),
            displayMode: 'full' as const,
            messagePayload: firstResponse.payload
          }
        };

        // Create new feed with the result
        setFeedSessions([{
          id: `feed-${Date.now()}`,
          sessionId: newSession.sessionId,
          feedItems: [resultItem],
          title: 'Launchpad'
        }]);
        setCurrentFeedIndex(0);
      }
    } catch (err) {
      console.error('[AIpad] Failed to execute launchpad command:', err);
    }
  }, [processCommand, createSession, sendMessage]);

  // Handler: Send message from input box
  const handleSendMessage = useCallback(async (message: string) => {
    console.log('[AIpad] Sending message from input:', message);

    // Check if it's a command first
    const isCommand = await processCommand(message);
    if (isCommand) {
      return; // Command was handled, don't send to backend
    }

    // Create new session and send message (clear screen approach for all queries)
    try {
      const newSession = await createSession();

      // Send message to backend
      const response = await sendMessage(newSession.sessionId, message);

      if (response.messages && response.messages.length > 0) {
        const firstResponse = response.messages[0];

        // Check for logout action in payload
        if (firstResponse.payload?.action === 'logout') {
          try {
            // Call logout API
            await logout();

            // Clear tenant context
            clearTenantSlug();

            // Wait for cookies to be cleared, then reload to startup screen
            await new Promise(resolve => setTimeout(resolve, 100));

            // Force reload to startup screen (clear all state)
            window.location.replace('/');
          } catch (err) {
            setError(`Abmeldung fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
            setTimeout(() => setError(null), 5000);
          }
          return;
        }

        // Check for UI Composition in payload
        if (firstResponse.payload?.format === 'ui' && firstResponse.payload?.uiComposition) {
          console.log('[AIpad] UI Composition received from backend:', firstResponse.payload.uiComposition);

          const uiComposition = firstResponse.payload.uiComposition as any;

          // Navigate to the UI Composition path based on key
          if (uiComposition.key === 'yardDocumentList') {
            console.log('[AIpad] Opening Yard Document List');
            navigate('/yard-list');
            return;
          }

          // For other UI Compositions, log warning
          console.warn('[AIpad] Unknown UI Composition key:', uiComposition.key);
        }

        // Regular chat message - Backend delivers HTML with iframe directly in content
        // Create feed item from response
        const resultItem: ChatMessageFeedItem = {
          id: firstResponse._id || `msg-${Date.now()}`,
          type: 'chat-message',
          title: 'Antwort',
          layout: { columnSpan: 12 },
          config: {
            question: message,
            answer: firstResponse.content,
            timestamp: new Date(firstResponse.createdAt || Date.now()),
            displayMode: 'full' as const,
            messagePayload: firstResponse.payload
          }
        };

        // Create NEW feed (clear screen, replace old content)
        const newFeed: FeedSession = {
          id: `feed-${Date.now()}`,
          sessionId: newSession.sessionId,
          feedItems: [resultItem],
          title: message
        };

        setFeedSessions([newFeed]); // Replace all feeds with new one
        setCurrentFeedIndex(0);
      }
    } catch (error) {
      console.error('[AIpad] Error sending message:', error);
      setError('Fehler beim Senden der Nachricht');
      setTimeout(() => setError(null), 3000);
    }
  }, [processCommand, createSession, sendMessage]);

  // Handler: Menu Button - Show history log feed
  const handleMenuButton = useCallback(async () => {
    console.log('[AIpad] Menu button clicked - showing history log feed');

    // Check if menu feed already exists
    const menuFeedExists = feedSessions.some(feed => feed.id === 'menu-feed');

    if (menuFeedExists) {
      // Navigate to existing menu feed
      const menuIndex = feedSessions.findIndex(feed => feed.id === 'menu-feed');
      setCurrentFeedIndex(menuIndex);
      return;
    }

    // Create history log feed with all chat sessions
    const historyCards: FeedItemDefinition[] = [];

    // Add each feed session as a tile in the history
    feedSessions.forEach((feed, index) => {
      if (feed.id !== 'menu-feed' && feed.sessionId) {
        // Get the first chat message from this feed as preview
        const chatItem = feed.feedItems.find(item => item.type === 'chat-message') as ChatMessageFeedItem | undefined;
        const preview = chatItem?.config.question || chatItem?.config.answer || 'Chat Session';

        historyCards.push({
          id: `history-${feed.id}`,
          type: 'tiles',
          title: feed.title,
          layout: { columnSpan: 12 },
          config: {
            columns: 2,
            tiles: [{
              id: feed.id,
              label: feed.title,
              description: preview.substring(0, 100) + (preview.length > 100 ? '...' : ''),
              action: {
                id: `navigate-${feed.id}`,
                type: 'command',
                target: 'navigateToFeed',
                params: { feedIndex: index }
              }
            }]
          }
        } as TileGridFeedItem);
      }
    });

    // If no history, show empty state
    if (historyCards.length === 0) {
      historyCards.push({
        id: 'no-history',
        type: 'tiles',
        title: 'Verlauf',
        layout: { columnSpan: 12 },
        config: {
          columns: 1,
          tiles: [{
            id: 'empty-history',
            label: 'Noch keine Chat-Verläufe',
            description: 'Starten Sie ein Gespräch über den runden Button',
            action: { id: 'noop', type: 'command', target: 'noop' }
          }]
        }
      } as TileGridFeedItem);
    }

    const menuFeed: FeedSession = {
      id: 'menu-feed',
      feedItems: historyCards,
      title: 'Verlauf'
    };

    setFeedSessions(prev => [...prev, menuFeed]);
    setCurrentFeedIndex(feedSessions.length);
  }, [feedSessions]);

  const executeCommand = useCallback((command: string) => {
    const commandLower = command.toLowerCase();

    if (commandLower === 'home') {
      handleHomeButton();
    } else if (commandLower === 'menu') {
      handleMenuButton();
    }
  }, [handleHomeButton, handleMenuButton]);

  // Handler: Center Button Click (Before Login)
  const handleCenterButtonClick = useCallback(() => {
    console.log('[AIpad] Center button clicked');

    // Check if user is already authenticated
    if (session?.authenticated && session.user) {
      console.log('[AIpad] User already authenticated, opening feed directly');
      setShowLoginPrompt(true); // Opens the feed/chat interface
    } else {
      console.log('[AIpad] User not authenticated, showing login options');
      setShowLoginPrompt(true); // Shows SSO login options
    }
  }, [session]);

  const handlePlusToggle = useCallback(() => {
    // Show contextual actions or new card creation
    console.log('Plus clicked in AIpad');
  }, []);

  const handleNotificationToggle = useCallback(() => {
    setFeedItems(prev => prev.filter(item =>
      item.id !== menuCardId &&
      item.id !== notificationCardId &&
      item.id !== newCardId
    ));

    if (notificationCardId) {
      setNotificationCardId(null);
    } else {
      const newNotificationCardId = `notifications-${Date.now()}`;
      const notificationCard: FeedItemDefinition = {
        id: newNotificationCardId,
        type: 'tiles',
        title: 'Benachrichtigungen',
        layout: { columnSpan: 12 },
        config: {
          columns: 1,
          tiles: [
            { id: 'no-notifications', label: 'Keine neuen Benachrichtigungen', icon: '?', action: { id: 'notification-action', type: 'command', target: 'noop' } }
          ]
        }
      };
      setFeedItems(prev => [...prev, notificationCard]);
      setNotificationCardId(newNotificationCardId);
    }
    setMenuCardId(null);
    setNewCardId(null);
  }, [menuCardId, notificationCardId, newCardId]);

  // Auto-scroll to new feed items
  useEffect(() => {
    if (feedItems.length === 0) return;

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      const feedContainer = document.querySelector('.aipad-feed');
      if (feedContainer) {
        // Scroll to bottom smoothly
        feedContainer.scrollTo({
          top: feedContainer.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [feedItems.length]);

  const handleTitleCommandExecute = useCallback(async (itemId: string, command: string) => {
    console.log('[AIpad] Title command execute:', itemId, command);

    const commandLower = command.toLowerCase();

    // Handle "set tenant" command - switch active tenant
    if (commandLower.startsWith('set tenant ')) {
      const tenantQuery = command.substring(11).trim();

      console.log('[AIpad] Set tenant command:', { tenantQuery, availableTenants });

      // Find tenant by name or ID
      const targetTenant = availableTenants.find(t =>
        t.tenantName.toLowerCase().includes(tenantQuery.toLowerCase()) ||
        t.tenantId === tenantQuery ||
        t.tenantSlug === tenantQuery
      );

      console.log('[AIpad] Target tenant found:', targetTenant);

      if (targetTenant) {
        console.log('[AIpad] Switching to tenant:', targetTenant.tenantName, targetTenant.tenantId);

        // Switch tenant immediately
        try {
          await handleTenantSwitch(targetTenant.tenantId);
          console.log('[AIpad] Tenant switch completed successfully');
        } catch (err) {
          console.error('[AIpad] Tenant switch failed:', err);
          alert(`Tenant-Wechsel fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
        }
      } else {
        console.log('[AIpad] Tenant not found:', tenantQuery);
        alert(`Tenant "${tenantQuery}" nicht gefunden. Verfügbare Tenants: ${availableTenants.map(t => t.tenantName).join(', ')}`);
      }
      return;
    }

    // Handle "tenant" command - show tenant list
    if (commandLower === 'tenant') {
      setFeedItems(prev => prev.map(item => {
        if (item.id === itemId) {
          return {
            id: itemId,
            type: 'tiles',
            title: 'Tenants',
            layout: { columnSpan: 12 },
            config: {
              columns: 3,
              tiles: availableTenants.map(tenant => ({
                id: tenant.tenantId,
                label: tenant.tenantName,
                badge: session?.user?.tenantId === tenant.tenantId ? '? Aktiv' : undefined,
                action: {
                  id: `switch-${tenant.tenantId}`,
                  type: 'command',
                  target: 'switchTenant',
                  params: { tenantId: tenant.tenantId }
                }
              }))
            }
          } as any;
        }
        return item;
      }));
      return;
    }

    if (commandLower.startsWith('list ')) {
      const commandText = command.substring(5).trim();
      let objectType = 'transports';

      if (commandText.includes('transport')) {
        objectType = 'transports';
      } else if (commandText.includes('yard') || commandText.includes('hof')) {
        objectType = 'yardDocuments';
      } else if (commandText.includes('user') || commandText.includes('benutzer')) {
        objectType = 'users';
      }

      // Show loading state
      setFeedItems(prev => prev.map(item => {
        if (item.id === itemId) {
          return {
            id: itemId,
            type: 'chat-message',
            title: 'Antwort',
            layout: { columnSpan: 12 },
            config: {
              question: command,
              answer: 'Lade Daten...'
            }
          } as ChatMessageFeedItem;
        }
        return item;
      }));

      try {
        const response = await fetch(`/api/${objectType}`, {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });

        if (!response.ok) throw new Error(`Fehler: ${response.status}`);

        const data = await response.json();
        const items = Array.isArray(data) ? data : (data.items || data.data || []);

        const forbiddenFields = ['type', 'id', '_id', 'tenantId', 'tenantid'];
        const allKeys = new Set<string>();
        items.forEach((row: any) => {
          Object.keys(row).forEach(key => {
            if (!forbiddenFields.includes(key)) {
              allKeys.add(key);
            }
          });
        });

        const firstRow = items[0] || {};
        const columns = Array.from(allKeys).map(key => ({
          id: key,
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
          field: key,
          align: typeof firstRow[key] === 'number' ? 'right' : 'left' as const
        }));

        setFeedItems(prev => prev.map(item => {
          if (item.id === itemId) {
            return {
              id: itemId,
              type: 'chat-message',
              title: 'Antwort',
              layout: { columnSpan: 12 },
              config: {
                question: command,
                answer: `Es wurden ${items.length} Eintr�ge gefunden.`,
                timestamp: new Date(),
                displayMode: 'full',
                kpiData: {
                  value: items.length,
                  label: 'Eintr�ge'
                },
                tableData: {
                  columns,
                  rows: items,
                  pagination: {
                    pageSize: 10,
                    pageSizeOptions: [10, 25, 50, 100]
                  }
                }
              }
            } as ChatMessageFeedItem;
          }
          return item;
        }));
      } catch (error) {
        setFeedItems(prev => prev.map(item => {
          if (item.id === itemId) {
            return {
              id: itemId,
              type: 'chat-message',
              title: 'Antwort',
              layout: { columnSpan: 12 },
              config: {
                question: command,
                answer: `? Fehler beim Laden: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`
              }
            } as ChatMessageFeedItem;
          }
          return item;
        }));
      }
      return;
    }

    // Default: send to AI and update the same card
    setFeedItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          title: command,
          type: 'text',
          config: { body: 'Wird verarbeitet...' }
        } as FeedItemDefinition;
      }
      return item;
    }));

    try {
      // Create session if needed
      let currentSessionId = sessionStack[sessionStack.length - 1]?.sessionId;
      if (!currentSessionId) {
        const newSession = await createSession();
        currentSessionId = newSession.sessionId;
        setSessionStack([{ sessionId: currentSessionId }]);
      }

      // Send message to AI
      const response = await sendMessage(currentSessionId, command);

      // Update item with AI response
      if (response.messages && response.messages.length > 0) {
        const lastMessage = response.messages[response.messages.length - 1];

        // Check for logout action in payload
        if ((lastMessage as any).payload?.action === 'logout') {
          try {
            // Call logout API
            await logout();

            // Clear tenant context
            clearTenantSlug();

            // Wait for cookies to be cleared, then reload to startup screen
            await new Promise(resolve => setTimeout(resolve, 100));

            // Force reload to startup screen (clear all state)
            window.location.replace('/');
          } catch (err) {
            setError(`Abmeldung fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
            setTimeout(() => setError(null), 5000);
          }
          return;
        }

        if (lastMessage.role === 'assistant') {
          setFeedItems(prev => prev.map(item => {
            if (item.id === itemId) {
              // Check if response contains documents (table data)
              const payload = (lastMessage as any).payload;
              const documents = payload?.documents || payload?.results;

              let tableData;
              if (documents && Array.isArray(documents) && documents.length > 0) {
                // Build table columns from document keys
                const forbiddenFields = ['type', 'id', '_id', 'tenantId', 'tenantid'];
                const allKeys = new Set<string>();
                documents.forEach((doc: any) => {
                  Object.keys(doc).forEach(key => {
                    if (!forbiddenFields.includes(key)) {
                      allKeys.add(key);
                    }
                  });
                });

                const firstDoc = documents[0];
                const columns = Array.from(allKeys).map(key => ({
                  id: key,
                  label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
                  field: key,
                  align: typeof firstDoc[key] === 'number' ? 'right' : 'left' as const
                }));

                tableData = {
                  columns,
                  rows: documents,
                  pagination: {
                    pageSize: 10,
                    pageSizeOptions: [10, 25, 50, 100]
                  }
                };
              }

              return {
                id: itemId,
                type: 'chat-message',
                title: 'Antwort',
                layout: { columnSpan: 12 },
                config: {
                  question: command,
                  answer: lastMessage.content,
                  displayMode: 'full',
                  tableData,
                  kpiData: payload?.total ? {
                    value: payload.total,
                    label: 'Einträge'
                  } : undefined
                }
              } as ChatMessageFeedItem;
            }
            return item;
          }));
        }
      }
    } catch (error) {
      console.error('Failed to execute command:', error);
      setFeedItems(prev => prev.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            type: 'text',
            config: { body: 'Fehler: Befehl konnte nicht ausgef�hrt werden.' }
          } as FeedItemDefinition;
        }
        return item;
      }));
    }
  }, [sessionStack, tiles, availableTenants, session]);

  const handleTitleFilter = useCallback((itemId: string, filterText: string) => {
    console.log('[AIpad] Title filter:', itemId, filterText);
    setTileFilters(prev => ({
      ...prev,
      [itemId]: filterText.toLowerCase()
    }));
  }, []);

  // Multi-Feed System Handlers
  // Handler: Round Button - Load recommendations without changing current display
  const handleCreateNewFeed = useCallback(async () => {
    console.log('[AIpad] Round button clicked - loading recommendations');

    try {
      setLoadingRecommendedActions(true);
      setError(null); // Clear any previous errors

      // Use existing session or create new one
      let sessionId = feedSessions[currentFeedIndex]?.sessionId;
      if (!sessionId) {
        const newSession = await createSession();
        sessionId = newSession.sessionId;
        console.log('[AIpad] Chat session created:', sessionId);
      }

      // Load recommended actions
      const context: RecommendedActionsContext = {
        sessionId: sessionId,
        currentRoute: location.pathname,
        recentMessages: allChatQuestions.slice(-5)
      };

      const actions = await getRecommendedActions(context);
      console.log('[AIpad] Recommended actions loaded:', actions.length);
      setRecommendedActions(actions);
      setLoadingRecommendedActions(false);

      console.log('[AIpad] Recommendations loaded successfully');
    } catch (error) {
      console.error('[AIpad] Failed to load recommendations:', error);

      // Provide detailed error message
      let errorMessage = 'Fehler beim Laden der Empfehlungen';
      if (error instanceof Error) {
        if (error.message.includes('fetch') || error.message.includes('network')) {
          errorMessage = 'Verbindung zum Server fehlgeschlagen. Bitte prüfen Sie, ob der Backend-Server läuft.';
        } else {
          errorMessage = `Fehler: ${error.message}`;
        }
      }

      setError(errorMessage);
      setLoadingRecommendedActions(false);
    }
  }, [feedSessions, currentFeedIndex, location.pathname, allChatQuestions, createSession]);

  const loadRecommendedActionsForCurrentContext = useCallback(async (sessionId?: string) => {
    setLoadingRecommendedActions(true);

    try {
      const currentFeed = feedSessions[currentFeedIndex];
      const context: RecommendedActionsContext = {
        sessionId: sessionId || currentFeed?.sessionId,
        currentRoute: location.pathname,
        recentMessages: allChatQuestions.slice(-5) // Last 5 questions
      };

      const actions = await getRecommendedActions(context);
      setRecommendedActions(actions);
    } catch (error) {
      console.error('[AIpad] Failed to load recommended actions:', error);
      // Fallback to default actions
      setRecommendedActions([
        'Zeige mir alle Transporte',
        'Welche Yard-Dokumente sind offen?',
        'Erstelle einen neuen Transport',
        'Zeige die letzten Aktivitäten'
      ]);
    } finally {
      setLoadingRecommendedActions(false);
    }
  }, [feedSessions, currentFeedIndex, location.pathname, allChatQuestions]);

  const handleNavigateToFeed = useCallback((direction: 'left' | 'right') => {
    if (direction === 'left' && currentFeedIndex > 0) {
      setCurrentFeedIndex(currentFeedIndex - 1);
    } else if (direction === 'right' && currentFeedIndex < feedSessions.length - 1) {
      setCurrentFeedIndex(currentFeedIndex + 1);
    }
  }, [currentFeedIndex, feedSessions.length]);

  const handleShowRecommendedActionsInCurrentFeed = useCallback(async () => {
    console.log('[AIpad] Showing recommended actions in current feed');

    const currentFeed = feedSessions[currentFeedIndex];
    if (!currentFeed?.sessionId) {
      console.error('[AIpad] No active session');
      return;
    }

    try {
      // Load recommended actions
      setLoadingRecommendedActions(true);
      const context: RecommendedActionsContext = {
        sessionId: currentFeed.sessionId,
        currentRoute: location.pathname,
        recentMessages: allChatQuestions.slice(-5)
      };

      const actions = await getRecommendedActions(context);
      setRecommendedActions(actions);
      setLoadingRecommendedActions(false);

      // Create recommended actions feed item
      const recommendedActionsFeedItem: RecommendedActionsFeedItem = {
        id: `recommended-actions-${Date.now()}`,
        type: 'recommended-actions',
        title: 'Was möchten Sie tun?',
        layout: { columnSpan: 12 },
        config: {
          actions: actions,
          onActionClick: async (action: string) => {
            // If it's an app, automatically add "start" prefix and directly navigate
            const actionObj = actions.find(a => typeof a === 'object' && a.text === action);
            if (actionObj && typeof actionObj === 'object' && actionObj.isApp) {
              // For apps: Direct navigation without creating feed item
              const commandText = `start ${action}`;
              await processCommand(commandText);
              return;
            }

            // For non-app actions: Submit to feed as before
            handleSubmitActionToFeed(currentFeed.sessionId!, action, currentFeedIndex);
          },
          chatHistory: allChatQuestions
        }
      };

      // Add to current feed
      setFeedSessions(prev => prev.map((feed, idx) =>
        idx === currentFeedIndex
          ? { ...feed, feedItems: [...feed.feedItems, recommendedActionsFeedItem] }
          : feed
      ));
    } catch (error) {
      console.error('[AIpad] Failed to show recommended actions:', error);
      setError('Fehler beim Laden der Vorschläge');
      setLoadingRecommendedActions(false);
    }
  }, [feedSessions, currentFeedIndex, location.pathname, allChatQuestions]);

  const handleSubmitAction = useCallback(async (action: string) => {
    console.log('[AIpad] Submitting action:', action);

    const currentFeed = feedSessions[currentFeedIndex];
    if (!currentFeed?.sessionId) {
      console.error('[AIpad] No active session');
      return;
    }

    await handleSubmitActionToFeed(currentFeed.sessionId, action, currentFeedIndex);
  }, [feedSessions, currentFeedIndex, handleSubmitActionToFeed]);

  const handleAction = useCallback(async (action: FeedAction, context?: FeedActionContext) => {
    console.log('[AIpad] Action triggered:', action);

    // Handle show-tiles action (from ObjectListCard click)
    if (action.type === 'show-tiles' && action.payload?.tiles) {
      console.log('[AIpad] Showing tiles:', action.payload);

      const tilesItem: FeedItemDefinition = {
        id: `tiles-${Date.now()}`,
        type: 'tiles',
        title: (action.payload.title as string) || 'Details',
        layout: { columnSpan: 12 },
        config: {
          columns: 3,
          tiles: (action.payload.tiles as Array<any>).map((tile: any) => ({
            ...tile,
            action: tile.action || { id: tile.id, type: 'command' as const, target: tile.id }
          }))
        }
      };

      // Add tiles to current feed
      setFeedSessions(prev => prev.map((feed, idx) =>
        idx === currentFeedIndex
          ? { ...feed, feedItems: [...feed.feedItems, tilesItem] }
          : feed
      ));
      return;
    }

    // Handle send-message action (from ObjectListCard click)
    if (action.type === 'send-message' && action.payload?.message) {
      const message = action.payload.message as string;
      console.log('[AIpad] Sending message from action:', message);

      try {
        // Check if it's a command first
        const isCommand = await processCommand(message);
        if (isCommand) {
          return; // Command was handled
        }

        // Create new session for fresh execution
        console.log('[AIpad] Creating new session for message');
        const newSession = await createSession();
        const currentSessionId = newSession.sessionId;

        console.log('[AIpad] Sending message to session:', currentSessionId);
        // Send message
        const response = await sendMessage(currentSessionId, message);
        console.log('[AIpad] Received response:', response);

        // Create new feed with response (replace old content)
        if (response.messages && response.messages.length > 0) {
          const assistantMsg = response.messages.find((msg: ChatMessage) => msg.role === 'assistant');

          // Check for logout action in payload
          if ((assistantMsg as any)?.payload?.action === 'logout') {
            console.log('[AIpad] Logout action received from backend - logging out user');

            try {
              // Call logout API
              await logout();

              // Clear tenant context
              clearTenantSlug();

              console.log('[AIpad] Logout successful - redirecting to login page');

              // Wait for cookies to be cleared, then reload to login page
              await new Promise(resolve => setTimeout(resolve, 100));

              // Force reload to login page (clear all state)
              window.location.replace('/');
            } catch (err) {
              console.error('[AIpad] Logout failed:', err);
              setError(`Abmeldung fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
              setTimeout(() => setError(null), 5000);
            }
            return;
          }

          if (assistantMsg) {
            console.log('[AIpad] Creating new feed with assistant response');

            // Create feed item from assistant message
            const resultItem: ChatMessageFeedItem = {
              id: `msg-${Date.now()}`,
              type: 'chat-message',
              title: 'Antwort',
              layout: { columnSpan: 12 },
              config: {
                question: message, // Show the original question
                answer: assistantMsg.content,
                timestamp: new Date(assistantMsg.createdAt || Date.now()),
                displayMode: 'full' as const,
                messagePayload: (assistantMsg as any).payload
              }
            };

            // Create new feed with only this result (clear screen)
            const newFeed: FeedSession = {
              id: `feed-${Date.now()}`,
              sessionId: currentSessionId,
              feedItems: [resultItem],
              title: message.length > 50 ? message.substring(0, 50) + '...' : message
            };

            setFeedSessions([newFeed]);
            setCurrentFeedIndex(0);
          }
        } else {
          console.warn('[AIpad] No messages in response');
        }
      } catch (error) {
        console.error('[AIpad] Error sending message:', error);
        setError('Fehler beim Senden der Nachricht');
        setTimeout(() => setError(null), 3000);
      }
      return;
    }

    if (action.type === 'command' && action.target === 'submitNewCard') {
      // Handle new card submission - execute the question as a command
      const question = action.payload?.question;
      if (question && action.paramKey && typeof action.paramKey === 'string' && typeof question === 'string') {
        handleTitleCommandExecute(action.paramKey, question);
      }
      return;
    }

    if (action.type === 'command' && action.target === 'cancelNewCard') {
      // Remove the new card
      if (action.paramKey) {
        setFeedItems(prev => prev.filter(item => item.id !== action.paramKey));
      }
      return;
    }

    if (action.type === 'navigate' && action.target) {
      // Remove the menu card if navigating from it
      if (context?.itemId) {
        setFeedItems(prev => prev.filter(item => item.id !== context.itemId));
      }

      // Show application in iframe card without changing the parent route
      const appTile = tiles.find(t => t.path === action.target);
      const appName = appTile?.title || action.target.split('/').pop() || 'Application';
      const appCard: FeedItemDefinition = {
        id: `app-${Date.now()}`,
        type: 'custom',
        title: appName,
        layout: { columnSpan: 12 },
        config: {
          render: () => (
            <div style={{ width: '100%', height: '80vh' }}>
              <iframe
                src={action.target}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
                style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px' }}
                title={appName}
                onLoad={(e) => {
                  // Prevent iframe navigation from affecting parent URL
                  try {
                    const iframe = e.target as HTMLIFrameElement;
                    if (iframe.contentWindow) {
                      // Override iframe's ability to change parent location
                      Object.defineProperty(iframe.contentWindow, 'top', {
                        get: () => iframe.contentWindow,
                        configurable: false
                      });
                    }
                  } catch (err) {
                    // Cross-origin restrictions may prevent this
                    console.warn('Could not sandbox iframe navigation:', err);
                  }
                }}
              />
            </div>
          )
        }
      };
      setFeedItems(prev => [...prev, appCard]);
      return;
    }

    if (action.type === 'command' && action.target === 'navigateToFeed') {
      // Navigate to a specific feed by index
      const feedIndex = action.params?.feedIndex;
      if (typeof feedIndex === 'number' && feedIndex >= 0 && feedIndex < feedSessions.length) {
        setCurrentFeedIndex(feedIndex);
      }
      return;
    }

    if (action.type === 'command' && action.target === 'switchTenant') {
      // Handle workspace switching from workspace tile
      const tenantId = action.params?.tenantId;
      if (tenantId && typeof tenantId === 'string') {
        // Remove workspace card immediately
        if (context?.itemId) {
          setFeedItems(prev => prev.filter(item => item.id !== context.itemId));
        }
        // Switch tenant
        handleTenantSwitch(tenantId);
      }
      return;
    }

    if (action.type === 'command' && action.target === 'showApps') {
      // Show applications menu
      const tenantName = availableTenants.find(t => t.tenantId === session?.user?.tenantId)?.tenantName || 'Anwendungen';
      const appsCard: FeedItemDefinition = {
        id: `apps-tiles-${Date.now()}`,
        type: 'tiles',
        title: tenantName,
        layout: { columnSpan: 12 },
        config: {
          columns: 3,
          tiles: tiles.map(tile => ({
            id: tile.id,
            label: tile.title,
            description: tile.description,
            action: {
              id: `launch-${tile.id}`,
              type: 'navigate',
              target: tile.path
            }
          }))
        }
      } as any;
      setFeedItems(prev => [...prev, appsCard]);
      return;
    }

    if (action.type === 'command' && action.target === 'showHome') {
      // Re-show home tiles
      const homeCard: FeedItemDefinition = {
        id: `home-tiles-${Date.now()}`,
        type: 'tiles',
        title: 'Anwendungen',
        layout: { columnSpan: 12 },
        config: {
          columns: 3,
          tiles: tiles.map(tile => ({
            id: tile.id,
            label: tile.title,
            description: tile.description,
            action: {
              id: `launch-${tile.id}`,
              type: 'navigate',
              target: tile.path
            }
          }))
        }
      } as any;
      setFeedItems(prev => [...prev, homeCard]);
    }

    // Handle camera image upload
    if (action.type === 'camera-image' && action.payload?.imageData) {
      console.log('[AIpad] Processing camera image upload');

      try {
        const { imageData, query, sessionId } = action.payload as {
          imageData: string;
          query: string;
          sessionId?: string;
        };

        // Send image to backend
        const response = await fetch('/api/chat/camera-image', {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            imageData,
            query,
            sessionId
          })
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const result = await response.json();

        // Generate HTML for multiple images if available
        let imagesHtml = '';
        if (result.imageUrls && Array.isArray(result.imageUrls) && result.imageUrls.length > 0) {
          // Multiple images from connector - show as thumbnail grid
          imagesHtml = `
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.5rem; margin-bottom: 1rem;">
              ${result.imageUrls.map((url: string, idx: number) =>
            `<img src="${url}" style="width:100%; height:150px; object-fit:cover; border-radius:8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); cursor:pointer;" alt="Bild ${idx + 1}" onclick="window.open(this.src, '_blank')" />`
          ).join('')}
            </div>
          `;
        } else if (result.imageUrl) {
          // Single image fallback (camera upload)
          imagesHtml = `<img src="${result.imageUrl}" style="max-width:100%; border-radius:4px; margin-bottom:1rem;" />`;
        }

        // Format text content: Convert markdown-style formatting to HTML
        let formattedAnalysis = result.analysis
          // Convert **bold** to <strong>
          .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
          // Convert --- separators to horizontal rules
          .replace(/\n---\n/g, '<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 1.5rem 0;" />')
          // Convert lines starting with "- " to list items
          .replace(/^- (.+)$/gm, '<li style="margin-bottom: 0.5rem;">$1</li>')
          // Wrap consecutive list items in <ul>
          .replace(/(<li[^>]*>.*?<\/li>\n?)+/gs, '<ul style="margin: 0.5rem 0; padding-left: 1.5rem; list-style-type: disc;">$&</ul>')
          // Convert double line breaks to paragraphs
          .replace(/\n\n/g, '</p><p style="margin: 0.75rem 0; line-height: 1.6; color: #374151;">')
          // Wrap entire content in paragraph
          .replace(/^(.+)$/s, '<p style="margin: 0.75rem 0; line-height: 1.6; color: #374151;">$1</p>');

        // Create new feed item with image analysis result
        const resultItem: ChatMessageFeedItem = {
          id: `camera-result-${Date.now()}`,
          type: 'chat-message',
          title: 'Bildanalyse',
          layout: { columnSpan: 12 },
          config: {
            question: query,
            answer: `<div>${imagesHtml}<div style="padding: 1.5rem; background: white; border-radius: 8px; font-family: system-ui, -apple-system, sans-serif; font-size: 0.95rem;">${formattedAnalysis}</div></div>`,
            timestamp: new Date(),
            displayMode: 'full' as const
          }
        };

        // Replace the camera upload card with the result
        setFeedSessions(prev => prev.map((feed, idx) => {
          if (idx === currentFeedIndex) {
            // Remove the last item (camera upload card) and add the result
            const updatedItems = [...feed.feedItems];
            updatedItems.pop(); // Remove camera upload card
            updatedItems.push(resultItem); // Add analysis result
            return { ...feed, feedItems: updatedItems };
          }
          return feed;
        }));

        console.log('[AIpad] Camera image analysis completed');
      } catch (error) {
        console.error('[AIpad] Camera image upload failed:', error);
        setError('Bildverarbeitung fehlgeschlagen');
        setTimeout(() => setError(null), 3000);
      }
      return;
    }
  }, [tiles, navigate, handleTitleCommandExecute, currentFeedIndex]);

  // Load session and tenants
  useEffect(() => {
    let mounted = true;

    fetchSession()
      .then(async (sessionData) => {
        if (!mounted) return;

        setSession(sessionData);
        if (sessionData?.authenticated && sessionData.user) {
          setUserName(sessionData.user.displayName || sessionData.user.email || "Benutzer");
          setUserEmail(sessionData.user.email || "");
          setWorkspaceName(sessionData.user.tenantId || "Workspace");

          // Create initial chat session
          try {
            const session = await createSession();
            const sessionId = session.sessionId;
            console.log('[AIpad] Created initial session:', sessionId);

            // Initialize feed with session
            setFeedSessions([{
              sessionId,
              title: 'Start',
              feedItems: [],
              isLoading: false
            }]);
            setCurrentFeedIndex(0);

            // Load recommended actions
            try {
              const context: RecommendedActionsContext = {
                sessionId,
                currentRoute: location.pathname,
                recentMessages: []
              };
              const actions = await getRecommendedActions(context);

              if (!mounted) return;

              // If backend returns no actions, use tiles as fallback
              let finalActions = actions;
              if (actions.length === 0 && tiles.length > 0) {
                console.log('[AIpad] No recommended actions from backend, using tiles as fallback');
                finalActions = tiles.map(tile => ({ text: tile.title, isApp: true }));
              }

              setRecommendedActions(finalActions);

              // Auto-open action popup after SSO if we have actions
              if (finalActions.length > 0) {
                setShowFooterSearch(true);
                console.log('[AIpad] Auto-opened action popup with', finalActions.length, 'actions');
              } else {
                console.log('[AIpad] No recommended actions available, chat button stays visible');
              }
            } catch (actionsError) {
              console.warn('[AIpad] Failed to load recommended actions:', actionsError);
              // Use tiles as fallback on error
              if (tiles.length > 0) {
                const fallbackActions = tiles.map(tile => ({ text: tile.title, isApp: true }));
                setRecommendedActions(fallbackActions);
                setShowFooterSearch(true);
                console.log('[AIpad] Using tiles as fallback after error, opened popup with', fallbackActions.length, 'actions');
              }
            }
          } catch (err) {
            console.error('Failed to initialize session:', err);
          }

          // Only fetch tenants if user is authenticated AND not a terminal
          const isTerminal = sessionData.user.roles?.includes('terminal');
          if (!isTerminal) {
            fetchAvailableTenants()
              .then(setAvailableTenants)
              .catch((err: Error) => {
                console.error("Failed to fetch tenants:", err);
              });
          }
        }
      })
      .catch((err) => {
        console.error("Failed to fetch session:", err);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Prevent iframe navigation from changing parent route
  useEffect(() => {
    const currentPath = window.location.pathname;

    const handlePopState = (e: PopStateEvent) => {
      // If we're not at the root path and have feed items, restore to root
      if (window.location.pathname !== '/' && feedItems.length > 0) {
        e.preventDefault();
        window.history.pushState(null, '', '/');
      }
    };

    // Lock the URL at root when feed items exist
    if (feedItems.length > 0 && currentPath !== '/') {
      window.history.replaceState(null, '', '/');
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [feedItems]);

  // Update current date/time every second
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const formatted = now.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      setCurrentDateTime(formatted);
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setShowUserMenu(false);
    setShowTenantSwitcher(false);
  }, [location.pathname]);

  // Close menus when clicking outside
  useEffect(() => {
    if (!showUserMenu && !showTenantSwitcher) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.aipad-user-menu') && !target.closest('.aipad-tenant-switcher')) {
        setShowUserMenu(false);
        setShowTenantSwitcher(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu, showTenantSwitcher]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
      clearTenantSlug();
      window.location.replace("/");
    } catch (err) {
      console.error("[AIpad] Logout failed:", err);
      const errorMessage = err instanceof Error ? err.message : "Unbekannter Fehler";
      alert(`Abmeldung fehlgeschlagen: ${errorMessage}`);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getInitials = (name: string | undefined, email: string | undefined): string => {
    if (name && name.trim().length > 0) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return "??";
  };

  const initials = session?.authenticated && session.user
    ? getInitials(session.user.displayName, session.user.email)
    : "??";

  const contextValue = useMemo(() => ({
    setPageTitle: (title: string, isEditing: boolean, editedValue?: string) => {
      setPageTitle(title);
      setIsEditingTitle(isEditing);
      if (editedValue !== undefined) {
        setEditedTitleValue(editedValue);
      }
    },
    setTitleHandlers: (handlers: typeof titleHandlers) => {
      setTitleHandlers(handlers);
    },
    setCardMenuActions: (actions: typeof cardMenuActions) => {
      setCardMenuActions(actions);
    },
    tiles
  }), [tiles]);

  // Get current tenant name from availableTenants
  const currentTenant = availableTenants.find(t => t.tenantId === session?.user?.tenantId);
  const tenantName = currentTenant?.tenantName || workspaceName || 'Workspace';

  // Get current feed or fall back to legacy feedItems
  const currentFeed = feedSessions[currentFeedIndex];
  const displayFeedItems = currentFeed?.feedItems || feedItems;

  // Filter feed items based on title filters
  const filteredFeedItems = useMemo(() => {
    return displayFeedItems.map(item => {
      const filterText = tileFilters[item.id];

      // If no filter for this item, return as-is
      if (!filterText) {
        return item;
      }

      // If item is tiles type, filter the tiles
      if (item.type === 'tiles') {
        const tileItem = item as TileGridFeedItem;
        const filteredTiles = tileItem.config.tiles.filter(tile => {
          const searchText = filterText.toLowerCase();
          return (
            tile.label.toLowerCase().includes(searchText) ||
            tile.description?.toLowerCase().includes(searchText)
          );
        });

        return {
          ...tileItem,
          config: {
            ...tileItem.config,
            tiles: filteredTiles
          }
        };
      }

      // For other types, return as-is
      return item;
    });
  }, [displayFeedItems, tileFilters]);

  // FIRST: Terminal Mode - Check if we're in terminal mode OR if we have terminal URL params
  const { tenant, app } = terminalParams;
  const hasTerminalParams = Boolean(tenant && app);
  const inTerminalMode = isTerminalMode() || hasTerminalParams;

  if (inTerminalMode) {
    // If authenticated, render terminal app immediately
    if (session?.authenticated && session.user?.tenantId) {
      if (!tenant || !app) {
        console.error("[Terminal Mode] Missing required parameters for terminal app");
        return (
          <div className="aipad-startup-container">
            <div className="aipad-error-toast">
              Terminal-Modus aktiviert, aber fehlende Parameter (tenant oder app).
            </div>
          </div>
        );
      }

      // Get Terminal App Component from registry
      const TerminalAppComponent = getAppComponent(session.user.tenantId, app);

      if (!TerminalAppComponent) {
        console.error("[Terminal Mode] App not found in registry:", {
          tenantId: session.user.tenantId,
          appName: app,
          lookupKey: `${session.user.tenantId}:${app}`
        });
        return (
          <div className="aipad-startup-container">
            <div className="aipad-error-toast">
              Terminal-App "{app}" nicht gefunden für Tenant "{tenant}".
            </div>
          </div>
        );
      }

      console.log("[Terminal Mode] Rendering app component:", app);

      // Render Terminal App
      return (
        <div className="aipad-terminal-container">
          <TerminalAppComponent />
        </div>
      );
    }

    // Not authenticated yet - render blank white screen instead of spinner
    // This prevents the spinner from showing while terminal auth is processing
    if (sessionLoading) {
      console.log("[Terminal Mode] Session loading, rendering blank screen (no spinner)");
      return <div style={{ width: '100vw', height: '100vh', background: '#ffffff' }}></div>;
    }

    // No token yet - either still authenticating or auth failed
    if (!session?.authenticated) {
      // Show error only if not loading
      if (!sessionLoading) {
        return (
          <div className="aipad-startup-container">
            <div className="aipad-error-toast">
              Terminal-Authentifizierung fehlgeschlagen.
            </div>
          </div>
        );
      }

      // Show spinner during initial authentication
      return (
        <div className="aipad-startup-container">
          <div className="aipad-startup-spinner"></div>
          <div className="aipad-powered-by">
            <span className="aipad-powered-by-text">powered by</span>
            <img src={IntecLogo} alt="IntecIO" className="aipad-powered-by-logo" />
          </div>
        </div>
      );
    }

    // Authenticated but no token in localStorage (shouldn't happen) - render terminal app
    // tenant and app are already declared at the top of the terminal mode block

    console.log("[Terminal Mode] Rendering terminal app (fallback path):", {
      tenant,
      app,
      tenantId: session.user?.tenantId
    });

    if (!tenant || !app || !session.user?.tenantId) {
      console.error("[Terminal Mode] Missing required parameters for terminal app");
      return (
        <div className="aipad-startup-container">
          <div className="aipad-error-toast">
            Terminal-Modus aktiviert, aber fehlende Parameter (tenant oder app).
          </div>
        </div>
      );
    }

    // Get Terminal App Component from registry
    const TerminalAppComponent = getAppComponent(session.user.tenantId, app);

    if (!TerminalAppComponent) {
      console.error("[Terminal Mode] App not found in registry:", {
        tenantId: session.user.tenantId,
        appName: app,
        lookupKey: `${session.user.tenantId}:${app}`
      });
      return (
        <div className="aipad-startup-container">
          <div className="aipad-error-toast">
            Terminal-App "{app}" nicht gefunden für Tenant "{tenant}".
          </div>
        </div>
      );
    }

    console.log("[Terminal Mode] Rendering app component (fallback path):", app);

    // Render Terminal App
    return (
      <div className="aipad-terminal-container">
        <TerminalAppComponent />
      </div>
    );
  }

  // Startup State: Show spinner while loading
  if (sessionLoading) {
    return (
      <div className="aipad-startup-container">
        <div className="aipad-startup-spinner"></div>
        <div className="aipad-powered-by">
          <span className="aipad-powered-by-text">powered by</span>
          <img src={IntecLogo} alt="IntecIO" className="aipad-powered-by-logo" />
        </div>
      </div>
    );
  }

  // Not logged in and haven't clicked button yet: Show only center button
  if (!session?.authenticated && !showLoginPrompt) {
    return (
      <div className="aipad-startup-container">
        <button
          className="aipad-startup-center-button"
          onClick={handleCenterButtonClick}
          title="Anmelden"
        >
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#ffffff" strokeWidth="2" fill="none"></circle>
            <circle cx="12" cy="12" r="6" fill="#ffffff"></circle>
          </svg>
        </button>
        <div className="aipad-powered-by">
          <span className="aipad-powered-by-text">powered by</span>
          <img src={IntecLogo} alt="IntecIO" className="aipad-powered-by-logo" />
        </div>
      </div>
    );
  }

  // Show login after button click
  if (!session?.authenticated && showLoginPrompt) {
    return (
      <div className="aipad-startup-container">
        <div className="aipad-login-card">
          <AuthLoginCard />
        </div>
        <div className="aipad-powered-by">
          <span className="aipad-powered-by-text">powered by</span>
          <img src={IntecLogo} alt="IntecIO" className="aipad-powered-by-logo" />
        </div>
      </div>
    );
  }

  // Authenticated: Show AIpad
  return (
    <LaunchpadContext.Provider value={contextValue}>
      <div className="aipad-container">
        <div className={`aipad-feed-area ${(loadingRecommendedActions || feedSessions[currentFeedIndex]?.isLoading) ? 'hidden-during-loading' : ''}`}>
          <FeedRenderer
            items={filteredFeedItems}
            onAction={handleAction}
            chatHistory={allChatQuestions}
            onTitleFilter={handleTitleFilter}
            onTitleCommandExecute={handleTitleCommandExecute}
          />

          {error && !terminalParams.apiKey && (
            <div className="aipad-error-toast">
              {error}
            </div>
          )}
        </div>

        {/* Recommendations Overlay - shows above content when search is active */}
        {showFooterSearch && (
          <div
            className={`aipad-recommendations-overlay ${isClosingPopup ? 'closing' : ''}`}
            onClick={closePopup}
          >
            <div
              className={`aipad-recommendations-popup ${isClosingPopup ? 'closing' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="aipad-recommendations-list">
                {recommendedActions
                  .filter(action => {
                    const actionText = typeof action === 'string' ? action : action.text;
                    return actionText.toLowerCase().includes(footerSearchText.toLowerCase());
                  })
                  .map((action, index) => {
                    const actionText = typeof action === 'string' ? action : action.text;
                    const isApp = typeof action === 'object' && action.isApp;
                    return (
                      <button
                        key={index}
                        className="aipad-recommendation-item"
                        onClick={async () => {
                          setFooterSearchText('');
                          closePopup();
                          const currentFeed = feedSessions[currentFeedIndex];
                          if (currentFeed?.sessionId) {
                            // Add "start" prefix for apps
                            const finalText = isApp ? `start ${actionText}` : actionText;
                            await handleSubmitActionToFeed(currentFeed.sessionId, finalText, currentFeedIndex);
                          }
                        }}
                      >
                        {actionText}
                      </button>
                    );
                  })}
              </div>
              {/* Search Bar as part of popup at bottom */}
              <div className="aipad-popup-search" style={{ position: 'relative' }}>
                <textarea
                  className="aipad-popup-search-input"
                  placeholder="Was möchten Sie tun?"
                  value={footerSearchText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFooterSearchText(value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';

                    // Update autocomplete suggestions
                    const filtered = getFilteredApps(value);
                    setAutocompleteApps(filtered);
                  }}
                  onKeyDown={async (e) => {
                    // Handle autocomplete navigation
                    if (showAutocomplete && autocompleteApps.length > 0) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setSelectedAutocompleteIndex(prev =>
                          prev < autocompleteApps.length - 1 ? prev + 1 : prev
                        );
                        return;
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setSelectedAutocompleteIndex(prev => prev > 0 ? prev - 1 : 0);
                        return;
                      } else if (e.key === 'Tab') {
                        e.preventDefault();
                        // Auto-complete with selected suggestion
                        setFooterSearchText(autocompleteApps[selectedAutocompleteIndex]);
                        setShowAutocomplete(false);
                        return;
                      }
                    }

                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();

                      // If autocomplete is shown and user presses Enter, use selected suggestion
                      let actionText = footerSearchText.trim();
                      if (showAutocomplete && autocompleteApps.length > 0) {
                        actionText = autocompleteApps[selectedAutocompleteIndex];
                      }

                      if (actionText) {
                        console.log('[AIpad] Enter pressed with text:', actionText);

                        // FIRST: Check if it's a command (console, start, etc.)
                        const isCommand = await processCommand(actionText);
                        console.log('[AIpad] Enter - processCommand returned:', isCommand);
                        if (isCommand) {
                          setFooterSearchText('');
                          setShowAutocomplete(false);
                          closePopup();
                          return;
                        }

                        // SECOND: Check if it's an app tile
                        const matchingTile = tiles.find(t => t.title === actionText);
                        if (matchingTile) {
                          // It's an app - directly navigate
                          const finalText = `start ${actionText}`;
                          await processCommand(finalText);
                        } else {
                          // Not an app - submit to feed
                          const currentFeed = feedSessions[currentFeedIndex];
                          if (currentFeed?.sessionId) {
                            await handleSubmitActionToFeed(currentFeed.sessionId, actionText, currentFeedIndex);
                          }
                        }
                        setFooterSearchText('');
                        setShowAutocomplete(false);
                        closePopup();
                      }
                    } else if (e.key === 'Escape') {
                      if (showAutocomplete) {
                        setShowAutocomplete(false);
                      } else {
                        closePopup();
                      }
                    }
                  }}
                  autoFocus
                  rows={1}
                />

                {/* Autocomplete Dropdown */}
                {showAutocomplete && autocompleteApps.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--aipad-bg)',
                    border: '1px solid var(--aipad-border)',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000
                  }}>
                    {autocompleteApps.map((app, index) => (
                      <div
                        key={app}
                        onClick={() => {
                          setFooterSearchText(app);
                          setShowAutocomplete(false);
                        }}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          backgroundColor: index === selectedAutocompleteIndex
                            ? 'var(--aipad-hover-bg, rgba(0,0,0,0.05))'
                            : 'transparent',
                          borderBottom: index < autocompleteApps.length - 1
                            ? '1px solid var(--aipad-border)'
                            : 'none',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={() => setSelectedAutocompleteIndex(index)}
                      >
                        <div style={{ fontWeight: 500 }}>{app}</div>
                        <div style={{ fontSize: '0.85em', opacity: 0.6, marginTop: '2px' }}>
                          App öffnen
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  className="aipad-popup-search-submit"
                  onClick={async () => {
                    let actionText = footerSearchText.trim();

                    // If autocomplete is shown, use selected suggestion
                    if (showAutocomplete && autocompleteApps.length > 0) {
                      actionText = autocompleteApps[selectedAutocompleteIndex];
                    }

                    if (actionText) {
                      console.log('[AIpad] Popup submit clicked with text:', actionText);

                      // FIRST: Check if it's a command (console, start, etc.)
                      const isCommand = await processCommand(actionText);
                      console.log('[AIpad] processCommand returned:', isCommand);
                      if (isCommand) {
                        setFooterSearchText('');
                        setShowAutocomplete(false);
                        closePopup();
                        return;
                      }

                      // SECOND: Check if it's an app tile
                      const matchingTile = tiles.find(t => t.title === actionText);
                      if (matchingTile) {
                        // It's an app - directly navigate
                        const finalText = `start ${actionText}`;
                        await processCommand(finalText);
                      } else {
                        // Not an app - submit to feed
                        const currentFeed = feedSessions[currentFeedIndex];
                        if (currentFeed?.sessionId) {
                          await handleSubmitActionToFeed(currentFeed.sessionId, actionText, currentFeedIndex);
                        }
                      }
                      setFooterSearchText('');
                      setShowAutocomplete(false);
                      closePopup();
                    }
                  }}
                  title="Absenden"
                >
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="#1E88E5" strokeWidth="2.5" fill="none"></circle>
                    <circle cx="12" cy="12" r="6.5" fill="#1E88E5"></circle>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AIpad Footer */}
        <footer className="aipad-footer">
          <div className="aipad-footer-content">
            <div className="aipad-footer-left">
            </div>

            <div className="aipad-footer-center">
              <button
                className={`aipad-footer-prompt-icon aipad-footer-center-button ${loadingRecommendedActions || showFooterSearch ? 'hidden' : ''}`}
                onClick={async () => {
                  if (!showFooterSearch) {
                    await handleCreateNewFeed();
                    setShowFooterSearch(true);
                  } else {
                    closePopup();
                  }
                }}
                title="Empfohlene Aktionen anzeigen"
                disabled={loadingRecommendedActions}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#1E88E5" strokeWidth="2.5" fill="none"></circle>
                  <circle cx="12" cy="12" r="6.5" fill="white" opacity="0.7"></circle>
                </svg>
              </button>
            </div>

            <div className="aipad-footer-right">
            </div>
          </div>
        </footer>
      </div>
    </LaunchpadContext.Provider>
  );
}
